import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Building2, ChevronDown, Loader2 } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@edemics/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@edemics/ui/dialog"
import { StandardInputField } from "./standard-input-field"
import { Button } from "@edemics/ui/button"
import {
  userManagementService,
  type SwitcherOption,
} from "@edemics/services/userManagementService"

interface Portal {
  id: string
  name: string
  description: string
  path: string
  requiresTenantSelect?: boolean
  personRole?: string // label for the second field, e.g. "Student"
}

const portals: Portal[] = [
  {
    id: "super-admin",
    name: "Super Admin Portal",
    description: "Platform-level administration",
    path: "/super-admin",
  },
  {
    id: "admin",
    name: "Admin Portal",
    description: "Full system administration",
    path: "/",
    requiresTenantSelect: true,
  },
  {
    id: "teacher",
    name: "Teacher Portal",
    description: "Academic management",
    path: "/dashboard/teacher",
    requiresTenantSelect: true,
    personRole: "Teacher",
  },
  {
    id: "parent",
    name: "Parent Portal",
    description: "Student progress tracking",
    path: "/dashboard/parent",
    requiresTenantSelect: true,
    personRole: "Parent",
  },
  {
    id: "student",
    name: "Student Portal",
    description: "Learning dashboard",
    path: "/dashboard/student",
    requiresTenantSelect: true,
    personRole: "Student",
  },
  {
    id: "alumni",
    name: "Alumni Portal",
    description: "Alumni network",
    path: "/dashboard/alumni",
    requiresTenantSelect: true,
    personRole: "Alumni",
  },
]

export function PortalSwitcher() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [currentPortal, setCurrentPortal] = useState("super-admin")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [targetPortal, setTargetPortal] = useState<Portal | null>(null)
  const [selectedTenant, setSelectedTenant] = useState("")
  const [selectedPerson, setSelectedPerson] = useState("")

  // Gate: only the actual authenticated super-admin account sees the switcher.
  // null = not yet resolved on the client (avoids SSR/hydration flash).
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null)

  // Real data + loading/error state
  const [tenants, setTenants] = useState<SwitcherOption[]>([])
  const [tenantsLoading, setTenantsLoading] = useState(false)
  const [tenantsError, setTenantsError] = useState("")

  const [people, setPeople] = useState<SwitcherOption[]>([])
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [peopleError, setPeopleError] = useState("")

  useEffect(() => {
    // Role of the ACTUAL logged-in account — read from the stored `user`
    // object (set at login), NOT the `userRole` active-portal flag.
    let accountRole = ""
    try {
      const raw = sessionStorage.getItem("user")
      if (raw) accountRole = (JSON.parse(raw)?.role || "").toLowerCase()
    } catch {
      accountRole = ""
    }
    setIsSuperAdmin(accountRole === "super-admin")

    const storedRole = sessionStorage.getItem("userRole") || "super-admin"
    setCurrentPortal(storedRole)
  }, [])

  // Load tenants when the dialog opens (fetch once, then cache in state)
  useEffect(() => {
    if (!dialogOpen || tenants.length > 0) return

    let cancelled = false
    setTenantsLoading(true)
    setTenantsError("")

    userManagementService
      .getTenants()
      .then((data) => {
        if (!cancelled) setTenants(data)
      })
      .catch((err) => {
        if (!cancelled) setTenantsError(err.message || "Failed to load tenants")
      })
      .finally(() => {
        if (!cancelled) setTenantsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dialogOpen, tenants.length])

  // Load people whenever a tenant is chosen for a person-based portal
  useEffect(() => {
    if (!targetPortal?.personRole || !selectedTenant) {
      setPeople([])
      return
    }

    let cancelled = false
    setPeopleLoading(true)
    setPeopleError("")
    setPeople([])

    userManagementService
      .getUsersByRole(selectedTenant, targetPortal.personRole)
      .then((data) => {
        if (!cancelled) setPeople(data)
      })
      .catch((err) => {
        if (!cancelled)
          setPeopleError(err.message || "Failed to load accounts")
      })
      .finally(() => {
        if (!cancelled) setPeopleLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedTenant, targetPortal?.personRole])

  const handlePortalSwitch = (portal: Portal) => {
    setIsOpen(false)
    if (portal.requiresTenantSelect) {
      setTargetPortal(portal)
      setSelectedTenant("")
      setSelectedPerson("")
      setPeople([])
      setPeopleError("")
      setDialogOpen(true)
      return
    }
    setCurrentPortal(portal.id)
    sessionStorage.setItem("userRole", portal.id)
    navigate(portal.path)
  }

  const handleTenantChange = (val: string) => {
    setSelectedTenant(val as string)
    setSelectedPerson("") // reset person when tenant changes
  }

  const isAdminOnly = targetPortal && !targetPortal.personRole

  const canConfirm = isAdminOnly
    ? !!selectedTenant
    : !!selectedTenant && !!selectedPerson

  const handleConfirm = () => {
    if (!targetPortal || !canConfirm) return
    setCurrentPortal(targetPortal.id)
    sessionStorage.setItem("userRole", targetPortal.id)
    localStorage.setItem("selectedTenant", selectedTenant)
    if (selectedPerson) localStorage.setItem("selectedPerson", selectedPerson)
    setDialogOpen(false)
    navigate(targetPortal.path)
  }

  const getCurrentPortal = () =>
    portals.find((p) => p.id === currentPortal) || portals[0]

  const personPlaceholder = (() => {
    if (!selectedTenant) return "Select a tenant first"
    if (peopleLoading) return "Loading accounts..."
    return `Search ${targetPortal?.personRole?.toLowerCase()}...`
  })()

  // Non-super-admins get no switcher at all. (Real enforcement lives in the
  // backend guard + route protection; this just hides the UI.)
  if (!isSuperAdmin) return null

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 rounded-lg bg-light-card-bg dark:bg-dark-card-bg border border-divider hover:bg-primary-light transition-colors focus:outline-none">
          <Building2 size={16} className="text-primary" />
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {getCurrentPortal().name}
            </span>
            <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Switch Portal</span>
          </div>
          <ChevronDown
            size={16}
            className={`text-light-text-secondary dark:text-dark-text-secondary transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-64 bg-light-card-bg dark:bg-dark-card-bg border border-divider">
          <DropdownMenuLabel className="text-light-text-primary dark:text-dark-text-primary">
            Available Portals
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-divider" />
          {portals.map((portal) => (
            <DropdownMenuItem
              key={portal.id}
              onClick={() => handlePortalSwitch(portal)}
              className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${
                currentPortal === portal.id
                  ? "bg-primary-light text-primary"
                  : "text-light-text-primary dark:text-dark-text-primary hover:bg-primary-light"
              }`}
            >
              <div className="flex items-center gap-2 w-full">
                <Building2 size={16} />
                <span className="font-medium">{portal.name}</span>
                {currentPortal === portal.id && (
                  <span className="ml-auto text-xs bg-primary text-white px-2 py-1 rounded">Current</span>
                )}
              </div>
              <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary ml-6">
                {portal.description}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Tenant (+ Person) Selection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-light-card-bg dark:bg-dark-card-bg border border-divider">
          <DialogHeader>
            <DialogTitle className="text-light-text-primary dark:text-dark-text-primary flex items-center gap-2">
              <Building2 size={18} className="text-primary" />
              Switch to {targetPortal?.name}
            </DialogTitle>
            <DialogDescription className="text-light-text-secondary dark:text-dark-text-secondary">
              {isAdminOnly
                ? "Choose the tenant school you are administering."
                : `Select the tenant first, then choose the ${targetPortal?.personRole} account to switch into.`}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-4">
            {/* Field 1 — Tenant */}
            <div>
              <StandardInputField
                label="Tenant School"
                type="link"
                value={selectedTenant}
                onChange={handleTenantChange}
                options={tenants}
                searchable
                placeholder={
                  tenantsLoading ? "Loading tenants..." : "Search tenant..."
                }
                disabled={tenantsLoading}
                required
              />
              {tenantsLoading && (
                <p className="mt-1 flex items-center gap-1 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                  <Loader2 size={12} className="animate-spin" />
                  Loading tenant schools...
                </p>
              )}
              {tenantsError && (
                <p className="mt-1 text-xs text-destructive">{tenantsError}</p>
              )}
            </div>

            {/* Field 2 — Person (teacher / parent / student / alumni) — only for non-admin portals */}
            {!isAdminOnly && (
              <div>
                <StandardInputField
                  label={targetPortal?.personRole ?? "Person"}
                  type="link"
                  value={selectedPerson}
                  onChange={(val) => setSelectedPerson(val as string)}
                  options={people}
                  searchable
                  placeholder={personPlaceholder}
                  disabled={!selectedTenant || peopleLoading}
                  required
                />
                {peopleLoading && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                    <Loader2 size={12} className="animate-spin" />
                    Loading {targetPortal?.personRole?.toLowerCase()} accounts...
                  </p>
                )}
                {peopleError && (
                  <p className="mt-1 text-xs text-destructive">{peopleError}</p>
                )}
                {!peopleLoading &&
                  !peopleError &&
                  selectedTenant &&
                  people.length === 0 && (
                    <p className="mt-1 text-xs text-light-text-secondary dark:text-dark-text-secondary">
                      No {targetPortal?.personRole?.toLowerCase()} accounts found
                      for this tenant.
                    </p>
                  )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-divider text-light-text-primary dark:text-dark-text-primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              Go to {targetPortal?.name}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}